import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import { ApiError } from "../client";
import { useApiClient } from "../context";
import type { components } from "../schema";
import { playlineStorage } from "../../playline/storage";
import { useModelSelector } from "../../features/debug/useModelSelector";

export type Playline = components["schemas"]["Playline"];
export type CreatePlaylineRequest =
  components["schemas"]["CreatePlaylineRequest"];

export type UseCreatePlaylineResult = UseMutationResult<
  Playline,
  ApiError,
  CreatePlaylineRequest
>;

// POST /playlines を叩いて新しい Playline を生成する mutation。
// openapi-fetch は { data, error } を返す API のため、エラー時は
// 明示的に throw して TanStack Query の onError 経路へ載せる。
// 成功した直後に playlineStorage へ id を書き込み、リロード後も
// 同じ playline を継続できるようにする。
export function useCreatePlayline(): UseCreatePlaylineResult {
  const client = useApiClient();
  const { selectedModel } = useModelSelector();

  return useMutation<Playline, ApiError, CreatePlaylineRequest>({
    mutationFn: async (body) => {
      const { data, error } = await client.POST("/playlines", {
        // OAS が CreatePlaylineRequest に model?: string をサポートしているため、
        // 選択中のモデルを付与してバックエンドに伝達する。
        body: { ...body, model: selectedModel },
      });
      if (error !== undefined) {
        // openapi-fetch の middleware で throw されない経路に備えた保険。
        // ここに来るのは型上 problem+json をパースした ProblemDetail のみ。
        throw new ApiError(
          typeof (error as { status?: number }).status === "number"
            ? (error as { status: number }).status
            : 0,
          error as unknown as ApiError["problem"],
        );
      }
      if (data === undefined) {
        // 200 系で body が空のケースは仕様上ありえないが、型ガードのため
        // 明示的にエラーへ倒す。
        throw new ApiError(500, {
          type: "about:blank",
          title: "Empty response",
          status: 500,
          detail: "createPlayline returned no data",
        });
      }
      return data;
    },
    onSuccess: (data) => {
      playlineStorage.set(data.id, data.scenarioId);
    },
  });
}
